import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminCountriesAPI, adminLocationsAPI } from '../services/api';
import Pagination from './Pagination';
import './Users.css';

const initialPerPage = () => {
  const stored = parseInt(localStorage.getItem('per_page') || '', 10);
  return [10, 15, 25, 50, 100].includes(stored) ? stored : 15;
};

const Locations = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: initialPerPage(),
    total: 0,
  });
  const [countryId, setCountryId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const prevDebouncedSearch = useRef('');

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminCountriesAPI.getAll();
        if (!cancelled) {
          setCountries(res.data?.countries || []);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error('Could not load countries for filter');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (countryId) params.country_id = countryId;

      const locRes = await adminLocationsAPI.getAll(params);
      const locationsData = locRes.data?.locations || [];
      setLocations(locationsData);

      const pg = locRes.data?.pagination;
      if (pg) {
        setPagination((prev) => {
          const nextPage = pg.page ?? prev.page;
          const nextPerPage = pg.per_page ?? prev.per_page;
          const nextTotal = pg.total ?? 0;
          if (
            prev.page === nextPage &&
            prev.per_page === nextPerPage &&
            prev.total === nextTotal
          ) {
            return prev;
          }
          return { ...prev, page: nextPage, per_page: nextPerPage, total: nextTotal };
        });
      }
    } catch (error) {
      toast.error('Error loading locations: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, debouncedSearch, countryId]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await adminLocationsAPI.delete(id);
      toast.success('Location deleted!');
      void fetchLocations();
    } catch (error) {
      toast.error('Error deleting location: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    prevDebouncedSearch.current = '';
    setCountryId('');
    setPagination((p) => ({ ...p, page: 1 }));
  };

  if (loading && locations.length === 0) {
    return <div className="loading">Loading locations...</div>;
  }

  return (
    <div className="users-container">
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <Link to="/locations/new" className="btn btn-primary" style={{ display: 'inline-block' }}>
          + Add Location
        </Link>
      </div>

      <div
        className="search-filter-bar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 220px' }}>
          <label htmlFor="locations-search" style={{ fontSize: '12px', fontWeight: 600 }}>
            Search (name, Arabic name, city)
          </label>
          <input
            id="locations-search"
            type="search"
            placeholder="Search…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ padding: '8px' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label htmlFor="locations-country" style={{ fontSize: '12px', fontWeight: 600 }}>
            Country
          </label>
          <select
            id="locations-country"
            value={countryId}
            onChange={(e) => {
              setCountryId(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ minWidth: '200px', padding: '8px' }}
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
          Clear filters
        </button>
      </div>

      {loading && locations.length > 0 ? (
        <div className="loading" style={{ padding: '8px' }}>
          Updating…
        </div>
      ) : null}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Arabic Name</th>
              <th>City</th>
              <th>Country</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && locations.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  No locations found
                </td>
              </tr>
            ) : (
              locations.map((location) => (
                <tr key={location.id}>
                  <td>{location.name || 'N/A'}</td>
                  <td>{location.name_ar || 'N/A'}</td>
                  <td>{location.city || 'N/A'}</td>
                  <td>
                    {location.country
                      ? `${location.country.code} · ${location.country.name}`
                      : 'N/A'}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate(`/locations/${location.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => handleDelete(location.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total > pagination.per_page && (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
          onPerPageChange={(per_page) => setPagination((p) => ({ ...p, per_page, page: 1 }))}
        />
      )}
    </div>
  );
};

export default Locations;
